import React from 'react';
import { Breadcrumbs, Box, Typography } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { CopTreeNode, CopTreeNodeSchema } from './useCopTreeNodes';
import { EditItemProps } from '../../../toolympus/api/useNewItem';
import { useItemWithControls } from '../../../toolympus/api/useWithControls';
import { SimpleDialog, FormGrid } from '../../../toolympus/components/primitives';
import { CopTreeNodeLinksPanel } from './CopTreeNodeLinksPanel';

interface Props extends Pick<EditItemProps<Partial<CopTreeNode>>, "item" | "update" | "errors" | "save"> {
  isOpen: boolean;
  close: () => void;
  isCreate?: boolean;
  ancestors?: CopTreeNode[];
}

export const CopTreeNodeEditDialog = (props: Props) => {
  const { isOpen, close, item, update, errors, save, isCreate, ancestors } = props;

  const { controls } = useItemWithControls(
    { data: item || {}, update, errors },
    { schema: CopTreeNodeSchema }
  );

  return (
    <SimpleDialog
      dialogTitle={isCreate ? "Add COP Tree Node" : "Edit COP Tree Node"}
      submitOnModEnter
      noFullscreen
      close={close}
      isOpen={isOpen}
      save={save}
      maxWidth="md"
    >
      {!!item && (
        <>
          {ancestors && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Location in Hierarchy:
              </Typography>
              <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="tree-path">
                {ancestors.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Root (Top Level)
                  </Typography>
                ) : (
                  ancestors.map(node => (
                    <Typography key={node._id} variant="body2" color="text.primary">
                      {node.title}
                    </Typography>
                  ))
                )}
                {isCreate && (
                  <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                    [New Node]
                  </Typography>
                )}
              </Breadcrumbs>
            </Box>
          )}

          <FormGrid columns="2fr 1fr">
            {controls([
              ["title", { controlProps: { autoFocus: true } }],
              ["type", { autoComplete: true }],
            ])}
          </FormGrid>

          <FormGrid columns="1fr 1fr 1fr 1fr">
            {controls([
              ["sort_order"],
              ["color"],
              ["is_hidden"],
              ["image_url"],
              isCreate ? null : ["created_by", { disabled: true }],
              isCreate ? null : ["created_at", { disabled: true, utcToLocal: true }],
              isCreate ? null : ["updated_by", { disabled: true }],
              isCreate ? null : ["updated_at", { disabled: true, utcToLocal: true }],
            ])}
          </FormGrid>

          <FormGrid columns="1fr">
            {controls([
              ["description"],
            ])}
          </FormGrid>

          {!isCreate && !!item._id &&
            <CopTreeNodeLinksPanel
              nodeId={item._id}
              />}
        </>
      )}
    </SimpleDialog>
  );
};